import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useAuthUser } from '../../contexts/AuthUserContext';
import { useCustomer } from '../../contexts/CustomerContext';
import { useNavigate } from 'react-router-dom';

/**
 * Customer Impersonation Component
 * 
 * Allows Helios admins to impersonate customers to view their data
 * and test their experience. Only visible to users with helios_admin role.
 */
const CustomerImpersonation = () => {
  const user = useAuthUser();
  const navigate = useNavigate();
  const {
    currentCustomer,
    isImpersonating,
    availableCustomers,
    switchCustomer,
    exitImpersonation,
    isLoading,
    error
  } = useCustomer();
  
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [switchError, setSwitchError] = useState(null);
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  // Debug logging
  // console.log('CustomerImpersonation - User:', user);
  // console.log('CustomerImpersonation - isHeliosAdmin:', user?.isHeliosAdmin);
  // console.log('CustomerImpersonation - availableCustomers:', availableCustomers);
  // console.log('CustomerImpersonation - isLoading:', isLoading);
  // console.log('CustomerImpersonation - error:', error);
  
  // Effect to reset local loading state when context changes
  useEffect(() => {
    if (!isLoading) {
      setIsLocalLoading(false);
    }
  }, [isLoading, isImpersonating]);

  // Only show for Helios admins
  if (!user?.isHeliosAdmin) {
    // console.log('CustomerImpersonation - Not showing because user is not Helios admin');
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="impersonation-panel button-container rounded-md p-2">
        <div className="flex items-center gap-2 text-sm text-accent">
          <Icon icon="eos-icons:loading" className="animate-spin" />
          Loading customers...
        </div>
      </div>
    );
  }

  // Show error state
  if (error || switchError) {
    return (
      <div className="impersonation-panel bg-red-100 border border-red-300 p-2 rounded-md">
        <div className="flex items-center gap-2 text-sm text-red-700">
          <Icon icon="mdi:alert-circle" />
          Error: {switchError || error}
        </div>
      </div>
    );
  }
  
  // Show local loading state for impersonation actions
  if (isLocalLoading) {
    return (
      <div className="impersonation-panel button-container rounded-md p-2">
        <div className="flex items-center gap-2 text-sm text-accent">
          <Icon icon="eos-icons:loading" className="animate-spin" />
          {isImpersonating ? "Exiting impersonation..." : "Impersonating customer..."}
        </div>
      </div>
    );
  }

  const handleCustomerSelect = (customerId) => {
    // console.log("CustomerImpersonation - handleCustomerSelect called with:", customerId);
    // console.log("CustomerImpersonation - currentCustomer?.id:", currentCustomer?.id);

    // Clear any previous errors
    setSwitchError(null);

    if (customerId && customerId !== currentCustomer?.id) {
      try {
        // console.log("CustomerImpersonation - calling switchCustomer with:", customerId);
        
        // Set local loading state to show loading indicator
        setIsLocalLoading(true);
        
        // Call the switchCustomer function
        switchCustomer(customerId).catch(err => {
          console.error("Error during customer impersonation:", err);
          setSwitchError(`Failed to impersonate customer: ${err.message || 'Unknown error'}`);
          setIsLocalLoading(false);
        });
        
        setSelectedCustomerId('');
        // Navigation is handled by switchCustomer, no need to navigate here
        setIsExpanded(false);
      } catch (err) {
        console.error("Error during customer impersonation:", err);
        setSwitchError(`Failed to impersonate customer: ${err.message || 'Unknown error'}`);
        setIsLocalLoading(false);
      }
    } else {
      // console.log("CustomerImpersonation - not calling switchCustomer because:", {
      //   customerId,
      //   currentCustomerId: currentCustomer?.id,
      //   condition: customerId && customerId !== currentCustomer?.id
      // });
    }
  };

  const handleExitImpersonation = () => {
    // Clear any previous errors
    setSwitchError(null);
    
    try {
      // Set local loading state to show loading indicator
      setIsLocalLoading(true);
      
      // Remove redundant navigation as exitImpersonation already handles it
      exitImpersonation().catch(err => {
        console.error("Error during exit impersonation:", err);
        setSwitchError(`Failed to exit impersonation: ${err.message || 'Unknown error'}`);
        setIsLocalLoading(false);
      });
      
      setIsExpanded(false);
    } catch (err) {
      console.error("Error during exit impersonation:", err);
      setSwitchError(`Failed to exit impersonation: ${err.message || 'Unknown error'}`);
      setIsLocalLoading(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="impersonation-panel">
      {isImpersonating ? (
        // Active impersonation state
        <div className="flex items-center justify-between gap-3 impersonation-active bg-primary-20 border-primary-50 border rounded-md pl-3 pr-2 py-2">
          <div className="flex items-center gap-2">
            <div className="text-sm flex flex-col gap-1">
              <div className="text-font-primary text-base flex items-center gap-1">
                <Icon icon="mdi:account-switch" className="responsive-icon-sm text-secondary" />
                <span className='text-font-primary font-bold w-fit lg:max-w-[100px] truncate whitespace-nowrap'>{currentCustomer?.name}</span>
              </div>
              {/* <div className="text-font-secondary text-xs">
                Customer ID: <span className='text-font-primary font-bold'>{currentCustomer?.id}</span>
              </div> */}
            </div>
          </div>
          <button
            onClick={handleExitImpersonation}
            className="flex items-center gap-1 p-1 text-xs rounded text-accent hover:bg-accent hover:text-background transition-colors"
            title="Return to admin view"
          >
            <Icon icon="material-symbols:logout-rounded" className='text-xl' />
          </button>
        </div>
      ) : (
        // Customer selection state - clean admin mode display
        <div className="customer-selector relative z-50">
          <button
            onClick={toggleExpanded}
            className="flex items-center justify-between w-full gap-6 px-2 py-2 text-sm button-container rounded-md group text-font-primary font-semibold transition-all duration-300 ease-out"
            title="Impersonate a customer"
          >
            <div className='flex items-center gap-1'>
              <Icon icon="mdi:shield-crown" className='responsive-icon-sm text-secondary group-hover:text-primary' />
              Impersonate
            </div>
            <Icon 
              icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"} 
              className="text-base text-accent group-hover:text-primary"
            />
          </button>

          {isExpanded && (
            <div className="mt-2 md:mt-3 h-fit top-full right-0 lg:left-0 absolute w-fit dropdown-container rounded-md">
              <div className="flex flex-col w-full">
                <label className="w-full whitespace-nowrap text-xs font-bold px-4 py-3">
                  Select Customer to Impersonate:
                </label>

                <hr className='border-border' />
                
                {availableCustomers.length > 0 ? (
                  <div className="flex flex-col w-full">
                    {availableCustomers.map(customer => (
                      <button
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer.id)}
                        className={`
                          w-full flex items-center group py-3 px-4 text-left text-xs hover:bg-primary-30 transition-all duration-300 ease-out rounded-sm
                          ${customer.id === currentCustomer?.id ? '' : ''}
                        `}
                        disabled={customer.id === currentCustomer?.id}
                      >
                        <div className='flex flex-row justify-between w-full gap-5 items-center'>
                          <div className={`group-hover:text-primary text-sm whitespace-nowrap ${customer.id === currentCustomer?.id ? 'text-primary font-bold' : 'text-font-secondary font-semibold'}`}>
                            {customer.name}
                          </div>
                          <div className="text-font-secondary whitespace-nowrap">
                            ID: <span className='text-font-primary'>{customer.id}</span> | Client: <span className='text-font-primary'>{customer.clientId}</span>
                          </div>
                        </div>
                        
                        {customer.id === currentCustomer?.id && (
                          <Icon icon="mdi:check" className="ml-1 text-lg text-accent" />
                        )}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-accent italic">
                    No customers available for impersonation
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerImpersonation;
